var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { t } from 'i18next';
import { ColorEnum } from './ColorPicker';
import ToolTip from './ToolTip';
import { useOnResize } from '../helper/hooks/onResize';
import { announce } from '../helper/announcer';
import '../styles/tokenSettingPanel.scss';
// Map ColorEnum keys to asset folder names
var COLOR_ASSET_MAP = {
    RED: 'red',
    YELLOW: 'yellow',
    BLUE: 'blue',
    BROWN: 'brown',
    ORANGE: 'orange',
    GREEN: 'green',
    PURPLE: 'purple',
    BLACK: 'black',
    WHITE: 'white',
    GRAY: 'gray',
};
// Map ColorEnum keys to translation keys
var COLOR_TOOLTIP_MAP = {
    RED: 'text.colorRed',
    YELLOW: 'text.colorYellow',
    BLUE: 'text.colorBlue',
    BROWN: 'text.colorBrown',
    ORANGE: 'text.colorOrange',
    GREEN: 'text.colorGreen',
    PURPLE: 'text.colorPurple',
    BLACK: 'text.colorBlack',
    WHITE: 'text.colorWhite',
    GRAY: 'text.colorGray',
};
// Map shape indices to translation keys (shape0..shape7 → text.shape1..text.shape8)
var SHAPE_TOOLTIP_MAP = {
    0: 'text.shape1',
    1: 'text.shape2',
    2: 'text.shape3',
    3: 'text.shape4',
    4: 'text.shape5',
    5: 'text.shape6',
    6: 'text.shape7',
    7: 'text.shape8',
};
// ShapeItem renders as role="radio" inside the shape radiogroup.
var ShapeItem = function (_a) {
    var color = _a.color, shapeIndex = _a.shapeIndex, isSelected = _a.isSelected, tabbable = _a.tabbable, onSelect = _a.onSelect, buttonRef = _a.buttonRef;
    var _b = useState(false), hovered = _b[0], setHovered = _b[1];
    var svgPath = "./assets/images/manipulative/numberFrames/".concat(color, "/shape").concat(shapeIndex, "/1.svg");
    var label = t(SHAPE_TOOLTIP_MAP[shapeIndex]);
    return (_jsxs("button", { type: "button", ref: buttonRef, className: "tokenShapeItem ".concat(isSelected ? 'selected' : ''), role: "radio", "aria-checked": isSelected, "aria-label": label, tabIndex: tabbable ? 0 : -1, onClick: onSelect, onMouseEnter: function () { return setHovered(true); }, onMouseLeave: function () { return setHovered(false); }, onFocus: function () { return setHovered(true); }, onBlur: function () { return setHovered(false); }, children: [_jsx("img", { src: svgPath, alt: "", draggable: "false" }), _jsx(ToolTip, { buttonHovered: hovered, text: label, direction: "top", isParentRelative: true })] }));
};
// ColorCircle renders as role="radio" inside the color radiogroup.
var ColorCircle = function (_a) {
    var colorKey = _a.colorKey, color = _a.color, isSelected = _a.isSelected, tabbable = _a.tabbable, onSelect = _a.onSelect, buttonRef = _a.buttonRef;
    var _b = useState(false), hovered = _b[0], setHovered = _b[1];
    var label = t(COLOR_TOOLTIP_MAP[colorKey]);
    return (_jsx("button", { type: "button", ref: buttonRef, className: "tokenColorCircle ".concat(isSelected ? 'selected' : ''), style: { backgroundColor: color }, role: "radio", "aria-checked": isSelected, "aria-label": label, tabIndex: tabbable ? 0 : -1, onClick: onSelect, onMouseEnter: function () { return setHovered(true); }, onMouseLeave: function () { return setHovered(false); }, onFocus: function () { return setHovered(true); }, onBlur: function () { return setHovered(false); }, children: _jsx(ToolTip, { buttonHovered: hovered, text: label, direction: "left", isParentRelative: true }) }));
};
var TokenSettingPanel = function (_a) {
    var onClose = _a.onClose, panelType = _a.panelType, tokenSelections = _a.tokenSelections, setTokenSelections = _a.setTokenSelections, _b = _a.inactive, inactive = _b === void 0 ? false : _b;
    var panelRef = useRef(null);
    var colorListRef = useRef(null);
    var colorBtnRefs = useRef(new Map());
    var shapeBtnRefs = useRef(new Map());
    // Detect when flexbox wraps to multiple columns and toggle .multi-column class
    var checkColumns = useCallback(function () {
        var el = colorListRef.current;
        var panel = panelRef.current;
        if (!el || !panel)
            return;
        // Remove multi-column to measure as single-column flexbox
        panel.classList.remove('multi-column');
        void el.offsetHeight; // force reflow
        var children = el.children;
        if (children.length < 2)
            return;
        var firstLeft = children[0].offsetLeft;
        var hasMulti = Array.from(children).some(function (child) { return child.offsetLeft !== firstLeft; });
        if (hasMulti) {
            panel.classList.add('multi-column');
        }
    }, []);
    useOnResize(checkColumns);
    var currentSelection = tokenSelections[panelType];
    var selectedColorAsset = currentSelection.color
        ? COLOR_ASSET_MAP[currentSelection.color]
        : null;
    var groupLabel = panelType === 'top' ? t('text.groupA') : t('text.groupB');
    // Stable color entry list (exclude DARK_NAVY_BLUE as per existing filter).
    var colorEntries = useMemo(function () { return Object.entries(ColorEnum).filter(function (_a) {
        var key = _a[0];
        return key !== 'DARK_NAVY_BLUE';
    }); }, []);
    var selectedColorIdx = useMemo(function () {
        return currentSelection.color
            ? colorEntries.findIndex(function (_a) {
                var k = _a[0];
                return k === currentSelection.color;
            })
            : -1;
    }, [currentSelection.color, colorEntries]);
    var selectedShapeIdx = useMemo(function () {
        if (!currentSelection.shape)
            return -1;
        var n = parseInt(currentSelection.shape.replace('shape', ''), 10);
        return Number.isFinite(n) ? n : -1;
    }, [currentSelection.shape]);
    // The radiogroup exposes a single tab stop — the checked radio, or the first
    // radio when none is checked. This matches the ARIA radiogroup pattern.
    var colorTabIdx = selectedColorIdx >= 0 ? selectedColorIdx : 0;
    var shapeTabIdx = selectedShapeIdx >= 0 ? selectedShapeIdx : 0;
    // Handle color selection (mouse or keyboard)
    var handleColorClick = useCallback(function (colorKey) {
        setTokenSelections(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[panelType] = {
                color: colorKey,
                shape: prev[panelType].shape, // Preserve existing shape selection
            }, _a)));
        });
    }, [panelType, setTokenSelections]);
    // Commit path (mouse click or Enter/Space/Tab on a color radio): update the
    // color, then move focus to the shape area so the natural Tab flow is
    // color → shape → tokens. Arrow keys only move focus; they don't commit.
    var handleColorCommit = useCallback(function (colorKey) {
        handleColorClick(colorKey);
        // Two rAFs: let React render the 8 shape radios (they only appear after
        // a color is chosen) before we try to focus one of them.
        requestAnimationFrame(function () {
            requestAnimationFrame(function () {
                // Prefer the already-selected shape; fall back to shape index 0
                // (the one the radiogroup marks tabbable when nothing is selected).
                var btns = Array.from(shapeBtnRefs.current.values());
                var selected = btns.find(function (btn) { return (btn === null || btn === void 0 ? void 0 : btn.getAttribute('aria-checked')) === 'true'; });
                var target = selected !== null && selected !== void 0 ? selected : shapeBtnRefs.current.get(0);
                target === null || target === void 0 ? void 0 : target.focus();
            });
        });
    }, [handleColorClick]);
    // Silent shape update — used both by arrow-key roving and by the commit handler.
    var handleShapeClick = useCallback(function (shapeIndex) {
        if (!currentSelection.color)
            return; // Guard: need a color first
        setTokenSelections(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[panelType] = __assign(__assign({}, prev[panelType]), { shape: "shape".concat(shapeIndex) }), _a)));
        });
    }, [currentSelection.color, panelType, setTokenSelections]);
    // Commit path (mouse click, or Enter/Space on a focused radio — both fire onClick):
    // update the shape, then move focus to the first DraggableToken in the matching
    // group so the user lands on "Add 1 Counter" ready to place on the frame.
    var handleShapeCommit = useCallback(function (shapeIndex) {
        handleShapeClick(shapeIndex);
        // Two rAFs: let React render the DraggableTokenList + inert flip before focusing.
        requestAnimationFrame(function () {
            requestAnimationFrame(function () {
                var group = document.querySelector(".token-group[data-group=\"".concat(panelType, "\"]"));
                // Don't jump focus into an inert group (e.g. no real frame yet).
                if (!group || group.hasAttribute('inert'))
                    return;
                var first = group.querySelector('.draggable-token');
                first === null || first === void 0 ? void 0 : first.focus();
            });
        });
    }, [handleShapeClick, panelType]);
    // Announce shape selection — "<Shape> graphic with <Color> color selected for <Group>"
    // Fires only when both color + shape are present and shape actually changed.
    var lastAnnouncedShapeRef = useRef(null);
    useEffect(function () {
        var _a, _b;
        if (inactive)
            return;
        if (!currentSelection.color || !currentSelection.shape)
            return;
        if (lastAnnouncedShapeRef.current === currentSelection.shape)
            return;
        lastAnnouncedShapeRef.current = currentSelection.shape;
        var colorLabel = t((_a = COLOR_TOOLTIP_MAP[currentSelection.color]) !== null && _a !== void 0 ? _a : '');
        var shapeLabel = t((_b = SHAPE_TOOLTIP_MAP[selectedShapeIdx]) !== null && _b !== void 0 ? _b : '');
        announce(t('text.tokenShapeSelected', {
            shape: shapeLabel,
            color: colorLabel,
            group: groupLabel,
        }));
    }, [currentSelection.shape, currentSelection.color, selectedShapeIdx, groupLabel, inactive]);
    // Escape from anywhere inside the token-setting panel closes the matching
    // token-setting-btn and returns focus to it. We trigger the button's native
    // click (re-using the existing toggle logic + audio) then focus it — the
    // button itself is outside the panel's `inert` region so the focus call
    // survives the state flip.
    var closeAndFocusToggle = useCallback(function () {
        var btn = document.querySelector(".tokenSettingBtnWrapper.token-setting-".concat(panelType, " .token-setting-btn"));
        if (!btn)
            return;
        btn.click();
        btn.focus();
    }, [panelType]);
    var handlePanelKeyDown = function (e) {
        if (e.key !== 'Escape')
            return;
        e.preventDefault();
        e.stopPropagation();
        closeAndFocusToggle();
    };
    // Arrow-key roving within the color radiogroup.
    var onColorKeyDown = function (e) {
        var n = colorEntries.length;
        // Forward Tab commits the focused color and advances focus to the shape
        // area (handleColorCommit already does the focus hop). Mirrors the shape
        // Tab → token-group pattern.
        if (e.key === 'Tab' && !e.shiftKey) {
            var focusedIdx_1 = -1;
            colorBtnRefs.current.forEach(function (btn, idx) {
                if (focusedIdx_1 === -1 && btn === e.target) {
                    focusedIdx_1 = idx;
                }
            });
            var toCommit = focusedIdx_1 >= 0 ? focusedIdx_1 : selectedColorIdx >= 0 ? selectedColorIdx : 0;
            e.preventDefault();
            e.stopPropagation();
            var key = colorEntries[toCommit][0];
            handleColorCommit(key);
            return;
        }
        // Arrow nav is relative to current focus, not selection — otherwise moving
        // focus off the selected radio and then arrowing would jump back.
        var curr = -1;
        colorBtnRefs.current.forEach(function (btn, idx) {
            if (curr === -1 && btn === e.target) {
                curr = idx;
            }
        });
        if (curr === -1)
            curr = selectedColorIdx >= 0 ? selectedColorIdx : 0;
        var next = curr;
        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowRight':
                next = (curr + 1) % n;
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                next = (curr - 1 + n) % n;
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = n - 1;
                break;
            default:
                return;
        }
        e.preventDefault();
        e.stopPropagation();
        // Arrow keys only move focus — Enter/Space/Tab commit the selection.
        requestAnimationFrame(function () { var _a; return (_a = colorBtnRefs.current.get(next)) === null || _a === void 0 ? void 0 : _a.focus(); });
    };
    // Arrow-key roving within the shape radiogroup.
    var onShapeKeyDown = function (e) {
        if (!selectedColorAsset)
            return; // shape area is empty-state
        // Forward Tab on a shape radio should commit the focused shape and advance
        // focus to the matching token-group — same effect as Enter/Space. Shift+Tab
        // falls through to native back-nav (→ color radiogroup).
        if (e.key === 'Tab' && !e.shiftKey) {
            // Find which shape button owns the event (fall back to the currently
            // selected or default shapeTabIdx).
            var focusedIdx_2 = -1;
            shapeBtnRefs.current.forEach(function (btn, idx) {
                if (focusedIdx_2 === -1 && btn === e.target) {
                    focusedIdx_2 = idx;
                }
            });
            var toCommit = focusedIdx_2 >= 0 ? focusedIdx_2 : selectedShapeIdx >= 0 ? selectedShapeIdx : 0;
            e.preventDefault();
            e.stopPropagation();
            handleShapeCommit(toCommit);
            return;
        }
        var n = 8;
        // Arrow nav is relative to current focus, not selection — otherwise moving
        // focus off the selected radio and then arrowing would jump back.
        var curr = -1;
        shapeBtnRefs.current.forEach(function (btn, idx) {
            if (curr === -1 && btn === e.target) {
                curr = idx;
            }
        });
        if (curr === -1)
            curr = selectedShapeIdx >= 0 ? selectedShapeIdx : 0;
        var next = curr;
        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowRight':
                next = (curr + 1) % n;
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                next = (curr - 1 + n) % n;
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = n - 1;
                break;
            default:
                return;
        }
        e.preventDefault();
        e.stopPropagation();
        // Arrow keys only move focus — Enter/Space/Tab commit the selection.
        requestAnimationFrame(function () { var _a; return (_a = shapeBtnRefs.current.get(next)) === null || _a === void 0 ? void 0 : _a.focus(); });
    };
    return (_jsxs("div", { className: "tokenSettingPanel", ref: panelRef, onClick: function (e) { return e.stopPropagation(); }, onKeyDown: handlePanelKeyDown, inert: inactive, children: [_jsx("div", { className: "tokenColorPalette", children: _jsx("div", { className: "tokenColorList", ref: colorListRef, role: "radiogroup", "aria-label": t('text.colorsForGroup', { group: groupLabel }), onKeyDown: onColorKeyDown, children: colorEntries.map(function (_a, idx) {
                        var key = _a[0], color = _a[1];
                        return (_jsx(ColorCircle, { colorKey: key, color: color, isSelected: currentSelection.color === key, tabbable: idx === colorTabIdx, onSelect: function () { return handleColorCommit(key); }, buttonRef: function (el) { return colorBtnRefs.current.set(idx, el); } }, key));
                    }) }) }), _jsxs("div", { className: "tokenShapeArea ".concat(!selectedColorAsset ? 'empty-state' : ''), role: "radiogroup", "aria-label": t('text.shapesForGroup', { group: groupLabel }), onKeyDown: onShapeKeyDown, children: [_jsx("div", { className: "tokenDividerV", "aria-hidden": "true" }), selectedColorAsset ? (
                    /* 4 rows x 2 columns - shape0 through shape7 */
                    [0, 1, 2, 3].map(function (row) { return (_jsxs("div", { children: [row > 0 && _jsx("div", { className: "tokenDividerH", "aria-hidden": "true" }), _jsxs("div", { className: "tokenShapeRow", children: [_jsx(ShapeItem, { color: selectedColorAsset, shapeIndex: row * 2, isSelected: currentSelection.shape === "shape".concat(row * 2), tabbable: row * 2 === shapeTabIdx, onSelect: function () { return handleShapeCommit(row * 2); }, buttonRef: function (el) { return shapeBtnRefs.current.set(row * 2, el); } }), _jsx(ShapeItem, { color: selectedColorAsset, shapeIndex: row * 2 + 1, isSelected: currentSelection.shape === "shape".concat(row * 2 + 1), tabbable: row * 2 + 1 === shapeTabIdx, onSelect: function () { return handleShapeCommit(row * 2 + 1); }, buttonRef: function (el) { return shapeBtnRefs.current.set(row * 2 + 1, el); } })] })] }, row)); })) : (_jsx("div", { className: "empty-state-message", children: t('text.selectAColor') }))] })] }));
};
export default TokenSettingPanel;
